/**
 * QorTrace · Landing — Iter237
 *
 * Block 3 — Use-case **tabbed switcher** replacing 3 static identical
 * cards. Left rail = persona list (selectable). Right pane = the
 * persona's workflow with a stylized quote, KPIs, and a tailored CTA.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Wallet, FileCode2, Building2 } from "lucide-react";


const PERSONAS = [
  {
    id: "individuals",
    name: "Individuals & Treasuries",
    icon: Wallet,
    eyebrow: "RETAIL · OTC · FAMILY OFFICES",
    question: "Is my wallet quantum-vulnerable today, and what should I do about it?",
    workflow: [
      "Paste a wallet address — no signup, no signature required.",
      "Get a 0–100 exposure score within 4 seconds.",
      "Receive a plain-English migration playbook tailored to your chain.",
    ],
    kpi: { value: "4s", label: "median scan time" },
    cta: { to: "/scan", label: "Scan a wallet free" },
  },
  {
    id: "protocols",
    name: "Protocols & DeFi Teams",
    icon: FileCode2,
    eyebrow: "ENGINEERING · SECURITY",
    question: "Where is ECDSA, ecrecover, and key-management risk in our contracts — and how do we migrate?",
    workflow: [
      "Upload Solidity sources or point at a GitHub repo.",
      "Get severity-tiered findings on every PQC migration surface, line-numbered.",
      "Ship a signed Cryptographic Migration Certificate buyers actually verify.",
    ],
    kpi: { value: "18+", label: "supported chains" },
    cta: { to: "/contract", label: "Analyze a contract" },
  },
  {
    id: "enterprises",
    name: "Exchanges, Custodians, Funds",
    icon: Building2,
    eyebrow: "REGULATED · INSTITUTIONAL",
    question: "Map and monitor cryptographic risk across our entire address surface, continuously.",
    workflow: [
      "Bulk import via CSV or API — millions of addresses across 18 chains.",
      "Aggregate balance-at-risk, exposed-address counts, and contract heatmaps.",
      "Webhook alerts on posture drift + NIST PQC compliance attestations.",
    ],
    kpi: { value: "NIST", label: "FIPS 203 / 204 / 205 aligned" },
    cta: { to: "/atlas", label: "Open Atlas" },
  },
];


export default function UseCaseTabs() {
  const [active, setActive] = useState(PERSONAS[0].id);
  const persona = PERSONAS.find((p) => p.id === active) || PERSONAS[0];
  const Ic = persona.icon;

  return (
    <div
      className="flex flex-col md:flex-row gap-6 lg:gap-8 items-stretch"
      style={{ minHeight: 420 }}
      data-testid="use-case-tabs"
    >
      {/* Tab list — left rail. */}
      <div className="flex flex-col gap-2 w-full md:w-[34%] shrink-0">
        {PERSONAS.map((p) => {
          const isActive = p.id === active;
          const PIc = p.icon;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setActive(p.id)}
              data-testid={`use-case-tab-${p.id}`}
              aria-pressed={isActive}
              className="text-left px-5 py-4 transition-all"
              style={{
                background: isActive ? "#0B1324" : "transparent",
                borderLeft: `2px solid ${
                  isActive ? "#1FDD83" : "rgba(255,255,255,0.06)"
                }`,
                color: isActive ? "#fff" : "var(--qt-text-2)",
                boxShadow: isActive
                  ? "inset 2px 0 0 0 #1FDD83, 0 0 0 1px rgba(31,221,131,0.18)"
                  : "none",
                cursor: "pointer",
                position: "relative",
              }}
            >
              <div className="flex items-center gap-3">
                <PIc size={18} style={{ color: isActive ? "#1FDD83" : "var(--qt-text-3)" }} />
                <div className="flex-1 min-w-0">
                  <div
                    className="qt-mono"
                    style={{
                      fontSize: 9.5, letterSpacing: "0.18em",
                      color: isActive ? "#1FDD83" : "var(--qt-text-3)",
                      fontWeight: 700, marginBottom: 4,
                    }}
                  >
                    {p.eyebrow}
                  </div>
                  <div
                    className="qt-h2"
                    style={{
                      fontSize: 17, lineHeight: 1.2,
                      color: isActive ? "#fff" : "var(--qt-text-2)",
                    }}
                  >
                    {p.name}
                  </div>
                </div>
                {isActive ? (
                  <ArrowRight size={14} style={{ color: "#1FDD83" }} />
                ) : null}
              </div>
            </button>
          );
        })}
      </div>

      {/* Content pane — right. */}
      <div
        key={persona.id}
        data-testid="use-case-content-pane"
        className="qt-card flex-1 relative overflow-hidden"
        style={{
          padding: "32px 36px",
          background: "#0B1324",
          border: "1px solid rgba(255,255,255,0.10)",
          animation: "qt-fade-slide-in 320ms ease both",
        }}
      >
        <style>{`
          @keyframes qt-fade-slide-in {
            from { opacity: 0; transform: translateX(10px); }
            to   { opacity: 1; transform: translateX(0); }
          }
        `}</style>

        {/* Watermark icon, top-right. */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute", top: 28, right: 32,
            color: "rgba(0,229,255,0.10)", pointerEvents: "none",
          }}
        >
          <Ic size={96} strokeWidth={1} />
        </div>

        {/* Big quote block. */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute", top: -32, left: 18,
            fontFamily: "Chivo, sans-serif", fontWeight: 800,
            fontSize: 200, lineHeight: 1,
            color: "rgba(0,229,255,0.06)",
            pointerEvents: "none", userSelect: "none",
          }}
        >
          “
        </div>

        <div className="relative z-10">
          <div
            className="qt-mono"
            style={{
              fontSize: 10, color: "#00E5FF",
              letterSpacing: "0.20em", textTransform: "uppercase",
              fontWeight: 700, marginBottom: 14,
            }}
          >
            THE QUESTION
          </div>
          <p
            className="qt-h2"
            style={{
              fontSize: 26, lineHeight: 1.3, color: "#fff",
              maxWidth: 620,
            }}
          >
            {persona.question}
          </p>

          <div
            className="qt-mono mt-10"
            style={{
              fontSize: 10, color: "var(--qt-text-3)",
              letterSpacing: "0.20em", textTransform: "uppercase",
              fontWeight: 700, marginBottom: 12,
            }}
          >
            HOW QORTRACE ANSWERS
          </div>
          <ol className="space-y-3 mb-8" style={{ maxWidth: 620 }}>
            {persona.workflow.map((w, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-sm"
                style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}
              >
                <span
                  className="qt-mono"
                  style={{
                    color: "#1FDD83", fontWeight: 700, fontSize: 11,
                    flexShrink: 0, marginTop: 2, letterSpacing: "0.10em",
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{w}</span>
              </li>
            ))}
          </ol>

          <div className="flex items-center gap-6 flex-wrap">
            <Link
              to={persona.cta.to}
              className="qt-btn qt-btn-primary"
              data-testid={`use-case-cta-${persona.id}`}
            >
              {persona.cta.label} <ArrowRight size={14} />
            </Link>
            <div className="flex items-baseline gap-2">
              <span
                className="qt-mono"
                style={{
                  fontSize: 32, fontWeight: 700, color: "#1FDD83",
                  letterSpacing: "-0.02em", lineHeight: 1,
                }}
              >
                {persona.kpi.value}
              </span>
              <span
                className="qt-mono"
                style={{
                  fontSize: 10, color: "var(--qt-text-3)",
                  letterSpacing: "0.16em", textTransform: "uppercase",
                  fontWeight: 700,
                }}
              >
                {persona.kpi.label}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
